import { useCallback, useEffect, useRef } from 'react';

/** Delay before a held button starts auto-repeating, and the repeat period. */
const HOLD_DELAY_MS = 400;
const HOLD_REPEAT_MS = 110;

/**
 * Press-and-hold helper for touch buttons. Wire the returned handler to
 * `onPointerDown`: it fires `action` once immediately (so a quick tap still
 * works), then keeps firing it every HOLD_REPEAT_MS while the finger stays
 * down. The release is caught on `window`, so the repeat still stops if the
 * button gets disabled mid-hold (e.g. the value hit its min/max).
 */
export const useHoldRepeat = (action: () => void): (() => void) => {
  // action is re-created each render; the ref keeps the repeat callback fresh.
  const actionRef = useRef(action);
  actionRef.current = action;
  const stateRef = useRef<{
    delay?: ReturnType<typeof setTimeout>;
    interval?: ReturnType<typeof setInterval>;
    onUp?: () => void;
  }>({});

  const stop = useCallback(() => {
    const s = stateRef.current;
    if (s.delay) clearTimeout(s.delay);
    if (s.interval) clearInterval(s.interval);
    if (s.onUp) {
      window.removeEventListener('pointerup', s.onUp);
      window.removeEventListener('pointercancel', s.onUp);
    }
    stateRef.current = {};
  }, []);

  const start = useCallback(() => {
    stop();
    actionRef.current(); // immediate first step — a quick tap still works
    const onUp = () => stop();
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    const delay = setTimeout(() => {
      stateRef.current.interval = setInterval(() => actionRef.current(), HOLD_REPEAT_MS);
    }, HOLD_DELAY_MS);
    stateRef.current = { delay, onUp };
  }, [stop]);

  useEffect(() => stop, [stop]); // clear timers/listeners on unmount
  return start;
};
